import { useEffect, useRef } from "react"

type ChatwootUser = { name?: string; email?: string; identifier_hash: string }

type Chatwoot = {
  hasLoaded: boolean
  /** Kept by the SDK after reset() and replayed to the reloaded frame. */
  user?: ChatwootUser
  identifier?: string
  toggle: (state: "close") => void
  toggleBubbleVisibility: (state: "show" | "hide") => void
  reset: () => void
  setUser: (identifier: string, user: ChatwootUser) => void
}

declare global {
  interface Window {
    chatwootSDK?: {
      run: (options: { baseUrl: string; websiteToken: string }) => void
    }
    $chatwoot?: Chatwoot
    /** Set by the admin server's /config.js when this environment enables Chatwoot. */
    __CHATWOOT__?: unknown
    chatwootSettings?: {
      hideMessageBubble: boolean
      showUnreadMessagesDialog: boolean
    }
  }
}

export type ChatwootConfig = {
  baseUrl: string
  websiteToken: string
  /** The localhost-only synthetic preview, which shows a banner. */
  localPreview?: boolean
}

const LOCAL_PREVIEW: ChatwootConfig = {
  baseUrl: "https://support.lambdacurry.dev",
  websiteToken: "yerYV3xUK15i9iLx96rgrZTo",
  localPreview: true,
}

const isRuntimeConfig = (
  value: unknown
): value is { baseUrl: string; websiteToken: string } => {
  if (typeof value !== "object" || value === null) return false
  const { baseUrl, websiteToken } = value as Record<string, unknown>
  return (
    typeof baseUrl === "string" &&
    /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(
      baseUrl
    ) &&
    typeof websiteToken === "string" &&
    /^[A-Za-z0-9]{8,64}$/.test(websiteToken)
  )
}

/**
 * Which Chatwoot widget this admin shows, if any: the environment's runtime config from /config.js (for example the
 * docs-bot inbox on dev), else the localhost preview, else none (Intercom).
 */
export const chatwootConfig = (
  previewFlag: string | undefined,
  hostname: string,
  runtime: unknown = window.__CHATWOOT__
): ChatwootConfig | undefined => {
  if (isRuntimeConfig(runtime))
    return { baseUrl: runtime.baseUrl, websiteToken: runtime.websiteToken }
  return isChatwootPreview(previewFlag, hostname) ? LOCAL_PREVIEW : undefined
}

export const isChatwootPreview = (
  enabled: string | undefined,
  hostname: string
) =>
  enabled === "true" &&
  (hostname === "localhost" || hostname.endsWith(".localhost"))

let sdk: Promise<void> | undefined
let sessionGeneration = 0
let resetOnReady = false
/**
 * True from reset() until the reloaded widget frame posts `loaded`. In Chatwoot v4.18.0, reset() navigates the
 * iframe but leaves `hasLoaded` true, and once reset it never dispatches `chatwoot:ready` again. A message posted
 * while the frame navigates (such as setUser) is lost, and setUser's dedupe cookie would then skip the retry. The
 * frame registers its message listeners just before posting `loaded`, so that message is when it can be identified.
 * If it never arrives, the bubble stays hidden.
 */
let reloading = false
/** The active session's sync, run when a reloaded frame is ready. */
let afterReload: (() => void) | undefined
let frameOrigin: string | undefined

const FRAME_ID = "chatwoot_live_chat_widget"
const FRAME_PREFIX = "chatwoot-widget:"

const onFrameMessage = (event: MessageEvent) => {
  if (!reloading || event.origin !== frameOrigin) return
  if (typeof event.data !== "string" || !event.data.startsWith(FRAME_PREFIX))
    return
  const frame = document.getElementById(FRAME_ID) as HTMLIFrameElement | null
  if (!frame || event.source !== frame.contentWindow) return
  let message: unknown
  try {
    message = JSON.parse(event.data.slice(FRAME_PREFIX.length))
  } catch {
    return
  }
  if ((message as { event?: unknown } | null)?.event !== "loaded") return
  reloading = false
  // Our listener runs before the SDK's own `loaded` handler (window.onmessage, set later by run()); let it finish.
  setTimeout(() => afterReload?.(), 0)
}

async function loadSDK(baseUrl: string) {
  if (!sdk) {
    frameOrigin = new URL(baseUrl).origin
    window.removeEventListener("message", onFrameMessage)
    window.addEventListener("message", onFrameMessage)
    sdk = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script")
      script.src = `${baseUrl}/packs/js/sdk.js`
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => {
        script.remove()
        sdk = undefined
        reject(new Error("Chatwoot preview could not load"))
      }
      document.head.appendChild(script)
    })
  }
  return sdk
}

/**
 * Who owns the widget's persisted visitor state: the verified contact identifier, or the admin's user id for an
 * anonymous visitor. Chatwoot keeps the visitor's conversation in the browser, so a different owner, or a session
 * that ended without an explicit logout, must reset it before the widget is shown.
 */
export const CHATWOOT_OWNER_KEY = "markethaus.chatwoot.owner"
/** Stored after a reset: no one owns the visitor state, so the next admin needs no second reset. */
export const CHATWOOT_RESET_OWNER = "reset"

const readOwner = () => {
  try {
    return window.localStorage.getItem(CHATWOOT_OWNER_KEY)
  } catch {
    return null
  }
}

const writeOwner = (owner: string) => {
  try {
    window.localStorage.setItem(CHATWOOT_OWNER_KEY, owner)
  } catch {
    // Storage can be unavailable (private mode); the explicit logout reset still applies.
  }
}

/** A Chatwoot contact identity signed by the API (`GET /admin/chatwoot/identity`) for the session's admin. */
export type ChatwootIdentity = {
  userId: string
  identifier: string
  identifierHash: string
  name?: string
  email?: string
}

/**
 * `pending` keeps the bubble hidden until the identity request settles; `anonymous` shows the widget without one
 * (identity not configured, or the request failed).
 */
export type ChatwootIdentityState = ChatwootIdentity | "pending" | "anonymous"

export type ChatwootIdentityResponse = {
  user_id: string
  identifier: string
  identifier_hash: string
  name?: string
  email?: string
}

/** Maps the identity request to widget state. Without the API secret (404) or after an error, it stays anonymous. */
export const chatwootIdentityState = (query: {
  status: "loading" | "error" | "success"
  data?: ChatwootIdentityResponse
}): ChatwootIdentityState => {
  if (query.status === "loading") return "pending"
  if (query.status === "error" || !query.data) return "anonymous"
  const { user_id, identifier, identifier_hash, name, email } = query.data
  if (!user_id || !identifier || !/^[0-9a-f]{64}$/.test(identifier_hash))
    return "anonymous"
  return {
    userId: user_id,
    identifier,
    identifierHash: identifier_hash,
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
  }
}

/** Whether Chatwoot has stored a visitor (contact token or identified user) in this browser. */
const hasVisitorState = (websiteToken: string) =>
  document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .some(
      (name) => name === "cw_conversation" || name === `cw_user_${websiteToken}`
    )

const closeAndReset = (widget: Chatwoot) => {
  widget.toggle("close")
  widget.toggleBubbleVisibility("hide")
  widget.reset()
  // The SDK would replay the previous admin's user object to the reloaded frame.
  delete widget.user
  delete widget.identifier
  reloading = true
}

export function useChatwootPreview(
  config: ChatwootConfig | undefined,
  userId?: string,
  identity: ChatwootIdentityState = "anonymous"
) {
  const baseUrl = config?.baseUrl
  const websiteToken = config?.websiteToken
  const identityRef = useRef(identity)
  identityRef.current = identity
  const syncRef = useRef<() => void>()

  useEffect(() => {
    if (!baseUrl || !websiteToken || !userId) return
    const generation = ++sessionGeneration
    let active = true
    let ready = false
    // Set once this session has reset the widget, so the owner check cannot reset it a second time.
    let resetThisSession = false

    /** Shows the widget for this session's admin, as its verified contact once the identity is known. */
    const sync = () => {
      const widget = window.$chatwoot
      if (!active || !ready || generation !== sessionGeneration) return
      if (!widget?.hasLoaded || reloading) return
      const current = identityRef.current
      if (current === "pending") return
      // An identity for another admin (a late response after an account switch) is never applied.
      if (current !== "anonymous" && current.userId !== userId) return
      const owner = current === "anonymous" ? userId : current.identifier
      const stored = readOwner()
      if (
        !resetThisSession &&
        stored !== owner &&
        stored !== CHATWOOT_RESET_OWNER &&
        hasVisitorState(websiteToken)
      ) {
        closeAndReset(widget)
        resetThisSession = true
        writeOwner(CHATWOOT_RESET_OWNER)
        // Identify and show once the reloaded frame is ready.
        return
      }
      if (current !== "anonymous") {
        widget.setUser(current.identifier, {
          identifier_hash: current.identifierHash,
          ...(current.name ? { name: current.name } : {}),
          ...(current.email ? { email: current.email } : {}),
        })
      }
      widget.toggleBubbleVisibility("show")
      writeOwner(owner)
    }

    const onReady = () => {
      if (generation !== sessionGeneration) {
        window.removeEventListener("chatwoot:ready", onReady)
        return
      }
      if (resetOnReady && window.$chatwoot?.hasLoaded) {
        closeAndReset(window.$chatwoot)
        resetOnReady = false
        resetThisSession = true
        writeOwner(CHATWOOT_RESET_OWNER)
      }
      if (active) {
        ready = true
        sync()
      } else window.removeEventListener("chatwoot:ready", onReady)
    }
    syncRef.current = sync
    afterReload = sync
    // A widget about to load with no stored visitor is pristine: nothing to reset.
    if (!window.$chatwoot && !hasVisitorState(websiteToken))
      writeOwner(CHATWOOT_RESET_OWNER)
    window.addEventListener("chatwoot:ready", onReady)
    window.chatwootSettings = {
      hideMessageBubble: true,
      showUnreadMessagesDialog: false,
    }
    loadSDK(baseUrl)
      .then(() => {
        if (!active) return
        window.chatwootSDK?.run({ baseUrl, websiteToken })
        if (window.$chatwoot?.hasLoaded) onReady()
      })
      .catch(() =>
        console.warn("Chatwoot preview could not load; reload to retry.")
      )

    return () => {
      active = false
      if (syncRef.current === sync) syncRef.current = undefined
      if (afterReload === sync) afterReload = undefined
      if (generation !== sessionGeneration || !window.$chatwoot) {
        window.removeEventListener("chatwoot:ready", onReady)
        return
      }
      // A newer session owns this reset if it starts before readiness.
      resetOnReady = true
      if (window.$chatwoot.hasLoaded) onReady()
    }
  }, [baseUrl, websiteToken, userId])

  // A resolved identity or a profile change (name, email) updates the contact without resetting the conversation.
  const identityKey =
    typeof identity === "string"
      ? identity
      : [
          identity.userId,
          identity.identifier,
          identity.identifierHash,
          identity.name ?? "",
          identity.email ?? "",
        ].join("\n")
  useEffect(() => {
    syncRef.current?.()
  }, [identityKey])
}
